// src/pages/CartPage.tsx
//
// The visible cart, finally. Reads via useCart() (proven this morning), lets the
// customer pick region/pickup (same options as ProductPage's checkout panel), and
// calls smartshop-checkout-v2 (multi-tenant: prices, shipping and tax come from
// shop_config/{gido}; price re-verified server-side, shipping
// escalation proven live on real large-box items).
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, SMARTSHOP_API, CLIENT_ID } from '../firebaseConfig';
import { useCart } from '../hooks/useCart';
import { useAuth } from '../hooks/useAuth';

const CREAM = '#F4F1E1';
const RED = '#8B1A1A';
const BLACK = '#0a0c0a';

const REGIONS = [
  { code: 'IE', label: 'Ireland' },
  { code: 'NI', label: 'Northern Ireland' },
  { code: 'EU', label: 'Europe' },
  { code: 'UK', label: 'United Kingdom' },
];

export default function CartPage() {
  const { cart, loadCart, error, updateItem } = useCart();
  const [region, setRegion] = useState('IE');
  const [pickup, setPickup] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const { signedIn } = useAuth();
  const navigate = useNavigate();

  // Save-for-later. Only possible once the cart is bound to an account, which happens
  // at the login moment (account-sync -> bind_and_merge_on_login). A signed-out
  // customer is sent to /account to register; their guest cart follows them there.
  const handleSave = async () => {
    if (!cart?.cart_id) return;
    setSaving(true); setSaveError(null);
    try {
      const res = await fetch(`${SMARTSHOP_API}/smartshop-cart-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          gido_id: CLIENT_ID,
          cart_id: cart.cart_id,
          session_id: localStorage.getItem('smartshop_session_id'),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save your cart');
      setSaved(true);
    } catch (e: any) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadCart();
  }, [loadCart]);

  const handleCheckout = async () => {
    if (!cart?.cart_id) return;
    setCheckingOut(true);
    setCheckoutError(null);
    try {
      const user = auth.currentUser;
      let headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (user) {
        const token = await user.getIdToken();
        headers['Authorization'] = `Bearer ${token}`;
      }
      const res = await fetch(`${SMARTSHOP_API}/smartshop-checkout-v2`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ cart_id: cart.cart_id, region, pickup }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.reasons ? data.reasons.join(', ') : data.error || 'Checkout failed');
      }
      window.location.href = data.url;
    } catch (e: any) {
      setCheckoutError(e.message);
    } finally {
      setCheckingOut(false);
    }
  };

  if (!cart) {
    return <div style={{ padding: 60, textAlign: 'center', color: 'rgba(0,0,0,0.4)' }}>Loading your cart...</div>;
  }

  if (cart.items.length === 0) {
    return (
      <div style={{ maxWidth: 480, margin: '60px auto', padding: '0 20px', textAlign: 'center' }}>
        <h1 style={{ color: RED, fontSize: 24, fontWeight: 900, fontStyle: 'italic',
                     fontFamily: 'Georgia, serif', marginBottom: 12 }}>Your Cart is Empty</h1>
        <p style={{ color: 'rgba(0,0,0,0.5)', fontSize: 13, marginBottom: 20 }}>
          Browse the shop and add something you like.
        </p>
        <button onClick={() => navigate('/shop')}
                style={{ background: RED, color: 'white', border: 'none', padding: '14px 28px',
                         fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.12em',
                         cursor: 'pointer' }}>
          Browse the Shop
        </button>
      </div>
    );
  }

  const currency = (cents: number) => `€${(cents / 100).toFixed(2)}`;

  return (
    <div style={{ maxWidth: 560, margin: '40px auto', padding: '0 20px' }}>
      <h1 style={{ color: RED, fontSize: 24, fontWeight: 900, fontStyle: 'italic',
                   fontFamily: 'Georgia, serif', marginBottom: 20 }}>Your Cart</h1>

      {cart.merge_notice && (
        <p style={{ fontSize: 12, color: 'rgba(0,0,0,0.6)', background: 'rgba(0,0,0,0.05)',
                    padding: '10px 14px', marginBottom: 16 }}>
          You already had a saved cart from last visit — we've combined them.
        </p>
      )}

      <div style={{ marginBottom: 24 }}>
      {cart.items.map((item, idx) => {
          const unit = (item.price_type === 'variable' ? item.variable_amount : item.price_snapshot) || 0;
          const lineTotal = unit * item.qty;
          const isVariable = item.price_type === 'variable';   // vouchers: qty locked at 1
          return (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                     padding: '14px 0', borderBottom: '1px solid rgba(0,0,0,0.10)', gap: 12 }}>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12 }}>
                {item.image_snapshot && (
                  <a href={item.slug ? `/product/${item.slug}` : undefined}
                     style={{ flexShrink: 0, display: 'block', width: 56, height: 56,
                              cursor: item.slug ? 'pointer' : 'default' }}>
                    <img src={item.image_snapshot} alt={item.name_snapshot}
                         style={{ width: 56, height: 56, objectFit: 'cover',
                                  borderRadius: 4, border: '1px solid rgba(0,0,0,0.10)' }} />
                  </a>
                )}
                <div style={{ minWidth: 0 }}>
                  {item.slug ? (
                    <a href={`/product/${item.slug}`}
                       style={{ color: BLACK, fontSize: 14, margin: 0, textDecoration: 'none' }}
                       onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                       onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}>
                      {item.name_snapshot}{item.variant_name_snapshot ? ` - ${item.variant_name_snapshot}` : ''}
                    </a>
                  ) : (
                    <p style={{ color: BLACK, fontSize: 14, margin: 0 }}>
                      {item.name_snapshot}{item.variant_name_snapshot ? ` - ${item.variant_name_snapshot}` : ''}
                    </p>
                  )}
                  <p style={{ color: 'rgba(0,0,0,0.5)', fontSize: 12, margin: '2px 0 0' }}>
                    {currency(unit)} each
                  </p>
                </div>
              </div>
              {!isVariable && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    aria-label="Decrease quantity"
                    disabled={item.qty <= 1}
                    onClick={() => updateItem(item.product_id, item.qty - 1, item.variant_id || undefined)}
                    style={{ width: 28, height: 28, background: 'rgba(0,0,0,0.04)', color: BLACK,
                             border: '1px solid rgba(0,0,0,0.20)', cursor: item.qty <= 1 ? 'not-allowed' : 'pointer',
                             fontSize: 16, lineHeight: 1, opacity: item.qty <= 1 ? 0.3 : 1 }}>
                    −
                  </button>
                  <span style={{ color: BLACK, fontSize: 14, minWidth: 20, textAlign: 'center' }}>{item.qty}</span>
                  <button
                    aria-label="Increase quantity"
                    onClick={() => updateItem(item.product_id, item.qty + 1, item.variant_id || undefined)}
                    style={{ width: 28, height: 28, background: 'rgba(0,0,0,0.04)', color: BLACK,
                             border: '1px solid rgba(0,0,0,0.20)', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>
                    +
                  </button>
                </div>
              )}
              <p style={{ color: BLACK, fontSize: 14, fontWeight: 700, margin: 0, minWidth: 70, textAlign: 'right' }}>
                {currency(lineTotal)}
              </p>
              <button
                aria-label="Remove item"
                onClick={() => updateItem(item.product_id, 0, item.variant_id || undefined)}
                style={{ background: 'transparent', color: 'rgba(0,0,0,0.35)', border: 'none',
                         cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '0 4px' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = RED)}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(0,0,0,0.35)')}>
                ×
              </button>
            </div>
          );
        })}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
          <input type="checkbox" checked={pickup} onChange={(e) => setPickup(e.target.checked)} />
          <span style={{ fontSize: 13, color: BLACK }}>Free In-Store Pickup — No1 St Kieran St, Kilkenny</span>
        </label>
        {!pickup && (
          <div>
            <p style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em',
                        color: 'rgba(0,0,0,0.4)', marginBottom: 8 }}>Ship to my address</p>
            <select value={region} onChange={(e) => setRegion(e.target.value)}
                    style={{ width: '100%', padding: '10px', background: 'rgba(0,0,0,0.05)',
                             border: '1px solid rgba(0,0,0,0.15)', color: BLACK, fontSize: 13 }}>
              {REGIONS.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 16, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ color: 'rgba(0,0,0,0.6)', fontSize: 13 }}>Subtotal</span>
          <span style={{ color: BLACK, fontSize: 13 }}>{currency(cart.total)}</span>
        </div>
        <p style={{ fontSize: 12, fontWeight: 400, color: 'rgba(0,0,0,0.45)', margin: 0 }}>
          Shipping calculated at checkout — final total shown on the Stripe payment page.
        </p>
        <p style={{ fontSize: 12, fontWeight: 700, color: 'rgba(0,0,0,0.55)', margin: '6px 0 0' }}>
          💬 You can add a note or question (colour, size, or a delivery request) on the payment page.
        </p>
      </div>

      <div style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.10)',
                    padding: '14px 16px', marginBottom: 20 }}>
        {signedIn ? (
          saved ? (
            <p style={{ margin: 0, fontSize: 13, color: BLACK }}>
              Saved. It will be here next time you sign in.
            </p>
          ) : (
            <>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: BLACK }}>
                Not ready to buy? Save this cart and pick it up later, on any device.
              </p>
              <button onClick={handleSave} disabled={saving}
                      style={{ background: 'none', color: BLACK, border: `1px solid ${RED}`,
                               padding: '10px 18px', fontWeight: 700, fontSize: 11,
                               textTransform: 'uppercase', letterSpacing: '0.1em',
                               cursor: saving ? 'wait' : 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving...' : 'Save for Later'}
              </button>
            </>
          )
        ) : (
          <>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: BLACK }}>
              Want to save this cart for later? Create an account - your cart comes with you.
            </p>
            <button onClick={() => navigate('/account?return=/cart')}
                    style={{ background: RED, color: 'white', border: 'none',
                             padding: '10px 18px', fontWeight: 700, fontSize: 11,
                             textTransform: 'uppercase', letterSpacing: '0.1em', cursor: 'pointer' }}>
              Register &amp; Save My Cart
            </button>
          </>
        )}
        {saveError && <p style={{ color: RED, fontSize: 12, margin: '10px 0 0' }}>{saveError}</p>}
      </div>

      {checkoutError && <p style={{ color: RED, fontSize: 12, marginBottom: 12 }}>{checkoutError}</p>}
      {error && <p style={{ color: RED, fontSize: 12, marginBottom: 12 }}>{error}</p>}

      <button onClick={handleCheckout} disabled={checkingOut}
              style={{ width: '100%', background: RED, color: 'white', border: 'none', padding: '16px',
                       fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.14em',
                       cursor: 'pointer', opacity: checkingOut ? 0.6 : 1 }}
              onMouseEnter={(e) => !checkingOut && (e.currentTarget.style.background = BLACK)}
              onMouseLeave={(e) => !checkingOut && (e.currentTarget.style.background = RED)}>
        {checkingOut ? 'Redirecting to payment...' : 'Proceed to Payment'}
      </button>
      <p style={{ fontSize: 10, color: 'rgba(0,0,0,0.35)', textAlign: 'center', margin: '10px 0 0',
                  textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        Secure payment via Stripe · VAT included
      </p>
    </div>
  );
}